import {
  ELSCommonConfig,
  ELSTokenHelper
} from '@els/els-ui-common-react';
import { get } from 'lodash';
import moment from 'moment';
import { convertIsbnsToNormalizedString } from '../../utilities/app.utilities';
import { scrubProps } from '../../utilities/analytics.utilities';
import {
  FALSE_VALUE,
  FEATURE_FLAG,
  TRUE_VALUE
} from '../../apis/eols-features-api/eols-features-api.constants';
import { UNKNOWN } from './app.constants';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { AppState } from './app-state.types';
import { OsmosisTokenDto } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import { InstitutionDto } from '../../apis/eols-course-crud/eols-course-crud.dtos';
import { RemediationAssignmentWithSourcesDto } from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';

export const getIsbns = (state: AppState): string[] => {
  if (!state.tokenIsbns) {
    return null;
  }
  return state.tokenIsbns.split(',');
};

export const getInstitution = (appState: AppState): InstitutionDto => {
  if (!appState.currentCourse) {
    return null;
  }
  return appState.currentCourse.institution;
};

const getUserEmailDomain = (appState: AppState): string => {
  const user = appState.eolsUser;
  const emailAddress = !user || !user.emailAddress ? ELSTokenHelper.getUserEmail() : user.emailAddress;
  if (!emailAddress) {
    return UNKNOWN;
  }
  const emailParts = emailAddress.split('@');
  if (!emailParts || emailParts.length !== 2) {
    return UNKNOWN;
  }
  return emailParts[1];
};

const isTestUser = (appState: AppState): string => {
  const emailDomain = getUserEmailDomain(appState);
  if (emailDomain === UNKNOWN) {
    return UNKNOWN;
  }

  const _isTestUser = ELSCommonConfig.testUserEmailDomains.some((domain) => {
    return emailDomain.includes(domain);
  });
  return _isTestUser ? TRUE_VALUE : FALSE_VALUE;
};

const getConsumerKey = (appState: AppState) => {
  return get(appState, 'currentCourse.consumerKey', '');
};
export const getDefaultActionProps = (appState: AppState): Record<string, string | number | string[]> => {
  const { evolveUser } = appState;
  const institution = appState.currentCourse ? appState.currentCourse.institution : null;
  const props = {
    courseSectionId: appState.courseSectionId,
    eolsUserId: appState.userId,
    evolveUserId: evolveUser ? evolveUser.externalUserId : UNKNOWN,
    userRole: appState.userRole,
    isbns: appState.tokenIsbns,
    isbnsSorted: convertIsbnsToNormalizedString(getIsbns(appState)),
    institution: institution ? institution.name : null,
    userEmailDomain: getUserEmailDomain(appState),
    isTestUser: isTestUser(appState),
    isMasqueradeUser: ELSTokenHelper.isMasqueradeUser(),
    pathname: window.location.pathname,
    appVersion: window.getBuildVersion ? window.getBuildVersion() : null,
    consumerKey: getConsumerKey(appState),
    ecrId: appState.ecrId
  };
  return scrubProps(props);
};

export const getIsMaintenance = (appState: AppState): boolean => {
  const value = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    appState.featureFlagsGrouped,
    FEATURE_FLAG.IS_SITE_DOWN_FOR_MAINTENANCE,
    appState.courseSectionId
  );
  return value === null ? false : value;
};

export const getIsAIChatSidebarEnabled = (
  appState: AppState,
  remediationAssignmentWithSources?: RemediationAssignmentWithSourcesDto
): boolean => {
  const assignmentSettings = remediationAssignmentWithSources?.remediationAssignment?.settings;

  const enableAiChat = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    appState.featureFlagsGrouped,
    FEATURE_FLAG.ENABLE_AI_CHAT,
    appState.courseSectionId
  );

  // Both feature flag and assignment setting must be enabled
  return enableAiChat && assignmentSettings?.enableSherpathAiChat === true;
};

export const getIsAIChatPageContextEnabled = (appState: AppState): boolean => {
  return getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    appState.featureFlagsGrouped,
    FEATURE_FLAG.ENABLE_AI_CHAT_PAGE_CONTEXT,
    appState.courseSectionId
  );
};

export const shouldGetNewOsmosisToken = (osmosisTokenDto: OsmosisTokenDto): boolean => {
  if (!osmosisTokenDto || !osmosisTokenDto.expires) {
    return true;
  }
  return moment.utc(osmosisTokenDto.expires).add({ hours: -10 }).isBefore(moment());
};
